import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { z } from 'zod'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronsUpDown,
  Columns3,
  CreditCard,
  ExternalLink,
  Link2,
  Link2Off,
  RefreshCw,
} from 'lucide-react'
import { StripeWebhookEventDetailSheet } from './-components/StripeWebhookEventDetailSheet'
import { StripeWebhookEventsTable } from './-components/StripeWebhookEventsTable'
import { StripeWebhookFilters } from './-components/StripeWebhookFilters'
import { StripeWebhookScopeTabs } from './-components/StripeWebhookScopeTabs'
import { HubspotInvoicePicker } from './-components/HubspotInvoicePicker'
import { StripeAutomationsList } from './-components/StripeAutomationsList'
import { StripeAutomationLogFilters } from './-components/StripeAutomationLogFilters'
import { StripeAutomationLogsTable } from './-components/StripeAutomationLogsTable'
import { StripeDatabaseAutomationsReport } from './-components/StripeDatabaseAutomationsReport'
import { StripeDriftFindingsTable } from './-components/StripeDriftFindingsTable'
import { ClientLinkCell } from './-components/shared'
import type {
  StripeAutomationLogsFilters,
  StripeCustomerBillingPlan,
  StripeCustomerRecord,
  StripeInvoiceRecord,
  StripePaymentRecord,
  StripeRecordsFilters,
  StripeSubscriptionRecord,
  StripeSyncBatchRecord,
  StripeSyncObject,
  StripeWebhookEventScope,
  StripeWebhookEventsFilters,
} from '@/types/stripe.types'
import { PageTitle } from '@/components/PageTitle'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/animate-ui/components/buttons/copy'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/animate-ui/components/radix/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/animate-ui/components/animate/tooltip'
import {
  useLinkStripeInvoiceHubspot,
  useReconcileStripeCustomer,
  useRunStripeDriftCheck,
  useSetStripeCustomerClient,
  useStripeAutomationLogs,
  useStripeAutomations,
  useStripeCustomers,
  useStripeDatabaseAutomations,
  useStripeDriftFindings,
  useStripeDriftStatus,
  useStripeInvoices,
  useStripePayments,
  useStripeSubscriptions,
  useStripeSyncStatus,
  useStripeWebhookEvents,
  useTriggerStripeSync,
  useWidenStripeSubscriptionRails,
} from '@/hooks/repositories/useStripeRepository'
import { useAllClients } from '@/hooks/repositories/useClientsRepository'
import {
  useSetDefaultPaymentMethod,
  useSetSubscriptionPaymentMethod,
} from '@/hooks/repositories/useBillingQueueRepository'
import { useDebouncedValue } from '@/hooks/use-cases/useDebouncedValue'
import { useAuth } from '@/contexts/AuthContext'
import { PaymentMethodPickerMenu } from '@/components/payment/PaymentMethodPickerMenu'
import { cn } from '@/lib/utils'
import { formatDateTime } from '@/lib/formatters'
import { queryKeys } from '@/lib/query-client'
import {
  environmentBadgeClass,
  formatCurrency,
  isBookkeepingCancel,
  isFailedPaymentStatus,
  linkageStatusBadgeClass,
  linkageStatusLabel,
  livemodeBadgeClass,
  paymentMethodLabel,
  paymentStatusLabel,
  readPaymentMethodId,
  stripeObjectUrl,
} from '@/lib/stripe-formatters'
import { requirePermission } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'

const tabSchema = z.object({
  tab: z
    .enum([
      'customers',
      'payments',
      'invoices',
      'subscriptions',
      'webhooks',
      'automations',
      'drift',
    ])
    .optional()
    .default('webhooks'),
  clientId: z.string().uuid().optional(),
  /** Seeds the Customers tab search (e.g. deep-linking to a Stripe customer id). */
  customerSearch: z.string().optional(),
  /** Nested sub-tab within the Automations tab. */
  autoTab: z.enum(['external', 'database']).optional().default('external'),
})

type StripeRouteSearch = z.infer<typeof tabSchema>

export const Route = createFileRoute('/_app/backoffice/integrations/stripe/')({
  component: RouteComponent,
  validateSearch: tabSchema,
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.stripe),
})

const PER_PAGE = 20

function latestStatusRefetch(status: StripeSyncBatchRecord | null | undefined) {
  return status && !status.completedAt
}

const SYNC_MODE_OPTIONS: Array<{
  mode: 'full' | 'backfill' | 'reconcile'
  label: string
  description: string
}> = [
  {
    mode: 'full',
    label: 'Sync Now (full)',
    description:
      'Backfill from Stripe, then reconcile local links. Does not delete anything.',
  },
  {
    mode: 'backfill',
    label: 'Backfill',
    description:
      'Re-read every record from Stripe and upsert it into the mirror. Does not delete anything.',
  },
  {
    mode: 'reconcile',
    label: 'Reconcile',
    description:
      'Re-link mirror rows to clients, checkouts, and plans from local data. Re-reads customers from Stripe; no other Stripe calls.',
  },
]

function SyncErrorsAffordance({ status }: { status: StripeSyncBatchRecord }) {
  const errors = status.errorDetails?.errors ?? []
  if (status.failureCount <= 0 || errors.length === 0) {
    return null
  }

  const preview = errors.slice(0, 5)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 gap-1 px-2 text-xs text-destructive hover:text-destructive"
        >
          <AlertTriangle className="size-3.5" />
          View errors
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-2" align="start">
        <p className="text-xs font-medium text-foreground">
          {status.failureCount} record{status.failureCount === 1 ? '' : 's'}{' '}
          failed
        </p>
        <ul className="space-y-1.5 text-xs text-muted-foreground">
          {preview.map((error, index) => (
            <li
              key={index}
              className="border-b border-border/50 pb-1.5 last:border-none last:pb-0"
            >
              <p className="text-foreground">{error.error}</p>
              {error.object ? <p>Object: {error.object}</p> : null}
            </li>
          ))}
        </ul>
        {errors.length > preview.length ? (
          <p className="text-xs text-muted-foreground">
            +{errors.length - preview.length} more not shown
          </p>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}

/**
 * Progress bar + counts for an active batch (sync or drift check). While the
 * job hasn't reported a total yet (`totalRecords` is 0), shows "Starting…"
 * with an empty bar instead of a misleading "0 / 0 · 0%" line.
 */
function BatchProgressBlock({
  totalRecords,
  successCount,
  failureCount,
  label,
}: {
  totalRecords: number
  successCount: number
  failureCount: number
  label: string
}) {
  if (totalRecords <= 0) {
    return (
      <div className="space-y-1.5">
        <p className="text-xs text-muted-foreground">Starting…</p>
        <Progress value={0} className="h-1.5" />
      </div>
    )
  }

  const processed = successCount + failureCount
  const progress = Math.min(100, Math.round((processed / totalRecords) * 100))

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{label}</span>
        <span>{progress}%</span>
      </div>
      <Progress value={progress} className="h-1.5" />
      <p className="text-xs text-muted-foreground">
        {processed} / {totalRecords} · {successCount} ok
        {' · '}
        {failureCount} failed
      </p>
    </div>
  )
}

function SyncToolbar({
  object,
  title,
  description,
  advancedItems,
}: {
  object: StripeSyncObject
  title: string
  description: string
  /**
   * Extra items for the Advanced menu, for actions that only make sense on one
   * object's tab. Rendered under a separator below the sync modes.
   */
  advancedItems?: React.ReactNode
}) {
  const triggerSync = useTriggerStripeSync(object)
  const { data: status, isLoading } = useStripeSyncStatus(object)

  const active = latestStatusRefetch(status) || triggerSync.isPending

  return (
    <Card className="border-border/70">
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <CardTitle className="text-sm font-semibold">{title}</CardTitle>
              {status?.stripeEnvironment ? (
                <Badge
                  variant="outline"
                  className={cn(
                    'text-xs',
                    environmentBadgeClass(status.stripeEnvironment),
                  )}
                >
                  {status.stripeEnvironment}
                </Badge>
              ) : null}
              <Badge variant="outline" className="text-xs">
                {active ? 'Sync active' : 'Idle'}
              </Badge>
            </div>
            <CardDescription className="text-xs">{description}</CardDescription>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              disabled={active}
              className="h-8 gap-1.5 text-xs"
              onClick={() => triggerSync.mutate('full')}
            >
              <RefreshCw className={cn('size-3.5', active && 'animate-spin')} />
              {active ? 'Syncing...' : 'Sync Now'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={active}
                  className="h-8 gap-1 text-xs"
                  aria-label="Advanced sync options"
                >
                  Advanced
                  <ChevronDown className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                {SYNC_MODE_OPTIONS.filter(
                  (option) => option.mode !== 'full',
                ).map((option) => (
                  <DropdownMenuItem
                    key={option.mode}
                    className="flex flex-col items-start gap-0.5 whitespace-normal py-2"
                    onSelect={() => triggerSync.mutate(option.mode)}
                  >
                    <span className="text-xs font-medium">{option.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  </DropdownMenuItem>
                ))}
                {advancedItems ? (
                  <>
                    <DropdownMenuSeparator />
                    {advancedItems}
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {active ? (
          <BatchProgressBlock
            totalRecords={status?.totalRecords ?? 0}
            successCount={status?.successCount ?? 0}
            failureCount={status?.failureCount ?? 0}
            label="Sync in progress…"
          />
        ) : isLoading ? (
          <p className="text-xs text-muted-foreground">
            Loading latest Stripe sync status…
          </p>
        ) : status ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              Last synced {formatDateTime(status.completedAt)} ·{' '}
              {status.successCount} ok
              {' · '}
              {status.failureCount} failed · {status.mode.toLowerCase()}
            </p>
            <SyncErrorsAffordance status={status} />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No Stripe sync batch has run for this object yet.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function PaginationFooter({
  page,
  totalPages,
  total,
  isLoading,
  onPageChange,
}: {
  page: number
  totalPages: number
  total: number
  isLoading: boolean
  onPageChange: (nextPage: number) => void
}) {
  return (
    <div className="flex items-center justify-between border-t px-4 py-3 text-xs text-muted-foreground">
      <span>{total} records</span>
      <div className="flex items-center gap-2">
        <span>
          Page {page} of {Math.max(1, totalPages)}
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={page <= 1 || isLoading}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={page >= totalPages || isLoading}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

function EmptyRows({
  label,
  colSpan = 6,
}: {
  label: string
  colSpan?: number
}) {
  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        className="py-10 text-center text-sm text-muted-foreground"
      >
        {label}
      </TableCell>
    </TableRow>
  )
}

function CustomerClientLinkPicker({
  onSelect,
  disabled,
}: {
  onSelect: (clientId: string) => void
  disabled?: boolean
}) {
  const { user } = useAuth()
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedValue(search, 300)

  const { data, isLoading } = useAllClients(
    user?.databaseId,
    { search: debouncedSearch, perPage: 20 },
    { enabled: open },
  )
  const clients = data?.items ?? []

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          className="h-7 gap-1.5 text-xs"
        >
          <Link2 className="size-3.5" />
          Link client
          <ChevronsUpDown className="size-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search clients..."
            className="text-xs"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Loading clients...' : 'No clients found.'}
            </CommandEmpty>
            <CommandGroup>
              {clients.map((client) => (
                <CommandItem
                  key={client.id}
                  value={client.id}
                  onSelect={() => {
                    onSelect(client.id)
                    setOpen(false)
                    setSearch('')
                  }}
                >
                  <Check className="mr-2 size-3.5 opacity-0" />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-xs font-medium">
                      {client.firstName} {client.lastName}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {client.email}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function ReconcileCustomerAction({ item }: { item: StripeCustomerRecord }) {
  const [open, setOpen] = React.useState(false)
  const reconcile = useReconcileStripeCustomer()
  const isMismatch = item.linkage.status === 'MISMATCH'

  const handleConfirm = () => {
    reconcile.mutate(item.id, {
      onSuccess: () => setOpen(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 gap-1.5 text-xs"
        >
          <RefreshCw className="size-3.5" />
          Reconcile
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reconcile billing profile</DialogTitle>
          <DialogDescription>
            {isMismatch
              ? `The client's billing profile currently points at a different Stripe customer (${item.linkage.billingProfileStripeCustomerId ?? 'unknown'}). Reconciling repoints it at ${item.stripeCustomerId}.`
              : `The linked client's billing profile has no Stripe customer id for the ${item.environment} environment. Reconciling points it at ${item.stripeCustomerId}.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={reconcile.isPending}
            onClick={handleConfirm}
          >
            {reconcile.isPending ? 'Reconciling…' : 'Reconcile'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CustomerLinkageCell({ item }: { item: StripeCustomerRecord }) {
  if (item.linkage.status === 'MATCHED') {
    if (!item.billingPlans.length) {
      return (
        <Badge
          variant="outline"
          className={cn(
            'text-xs',
            linkageStatusBadgeClass(item.linkage.status),
          )}
        >
          {linkageStatusLabel(item.linkage.status)}
        </Badge>
      )
    }

    return (
      <div className="flex flex-wrap gap-1">
        {item.billingPlans.map((plan) => (
          <Badge
            key={plan.id}
            variant="outline"
            className="border-emerald-500/30 bg-emerald-500/10 text-xs text-emerald-700 dark:text-emerald-400"
          >
            {plan.name}
          </Badge>
        ))}
      </div>
    )
  }

  const badge = (
    <Badge
      variant="outline"
      className={cn('text-xs', linkageStatusBadgeClass(item.linkage.status))}
    >
      {linkageStatusLabel(item.linkage.status)}
    </Badge>
  )

  if (item.linkage.status !== 'MISMATCH') {
    return badge
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{badge}</span>
        </TooltipTrigger>
        <TooltipContent>
          Profile points at{' '}
          {item.linkage.billingProfileStripeCustomerId ?? 'unknown'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function CustomerActionsCell({ item }: { item: StripeCustomerRecord }) {
  const setClient = useSetStripeCustomerClient()

  const handleLink = (clientId: string) => {
    setClient.mutate({ id: item.id, clientId })
  }

  const handleUnlink = () => {
    setClient.mutate({ id: item.id, clientId: null })
  }

  if (item.linkage.status === 'MATCHED') {
    return null
  }

  if (item.linkage.status === 'NO_CLIENT') {
    return (
      <CustomerClientLinkPicker
        onSelect={handleLink}
        disabled={setClient.isPending}
      />
    )
  }

  // MISSING or MISMATCH — client is linked, offer reconcile + unlink/relink.
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <ReconcileCustomerAction item={item} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={setClient.isPending}
            className="h-7 gap-1 text-xs"
          >
            <ChevronDown className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="text-xs">
          <DropdownMenuItem className="text-xs" onSelect={handleUnlink}>
            <Link2Off className="size-3.5" />
            Unlink client
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

const STRIPE_RECORDS_SORT_OPTIONS: Array<{
  value: NonNullable<StripeRecordsFilters['sort']>
  label: string
}> = [
  { value: 'created_desc', label: 'Newest' },
  { value: 'created_asc', label: 'Oldest' },
  { value: 'synced_desc', label: 'Recently synced' },
]

type CustomerColumnKey =
  | 'email'
  | 'balance'
  | 'defaultMethod'
  | 'created'
  | 'totalSpend'
  | 'subscriptions'
  | 'name'
  | 'description'
  | 'country'
  | 'hubspotContactLink'
  | 'coupon'
  | 'currency'
  | 'delinquent'
  | 'synced'
  | 'payments'
  | 'firstPayment'
  | 'lastPayment'
  | 'refundedVolume'

type CustomerColumnConfig = {
  key: CustomerColumnKey
  label: string
  defaultVisible: boolean
  headClassName?: string
  cellClassName?: string
  render: (item: StripeCustomerRecord) => React.ReactNode
}

const CUSTOMER_COLUMN_VISIBILITY_STORAGE_KEY =
  'stripe-customers-column-visibility'

const CUSTOMER_COLUMNS: Array<CustomerColumnConfig> = [
  {
    key: 'email',
    label: 'Email',
    defaultVisible: true,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => item.email ?? '—',
  },
  {
    key: 'balance',
    label: 'Balance',
    defaultVisible: true,
    cellClassName: 'text-xs',
    render: (item) =>
      item.balance < 0 ? (
        <span className="text-emerald-700 dark:text-emerald-400">
          {formatCurrency(Math.abs(item.balance) / 100, item.currency ?? 'USD')}{' '}
          credit
        </span>
      ) : (
        formatCurrency(item.balance / 100, item.currency ?? 'USD')
      ),
  },
  {
    key: 'defaultMethod',
    label: 'Default method',
    defaultVisible: true,
    cellClassName: 'text-xs',
    render: (item) => paymentMethodLabel(item.defaultPaymentMethodSummary),
  },
  {
    key: 'created',
    label: 'Created',
    defaultVisible: true,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => formatDateTime(item.stripeCreatedAt),
  },
  {
    key: 'totalSpend',
    label: 'Total spend',
    defaultVisible: true,
    cellClassName: 'text-xs',
    render: (item) => formatCurrency(item.totalSpend, item.currency ?? 'USD'),
  },
  {
    key: 'subscriptions',
    label: 'Subscriptions',
    defaultVisible: true,
    cellClassName: 'text-xs',
    render: (item) => item.subscriptionsCount,
  },
  {
    key: 'name',
    label: 'Name',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => item.name ?? '—',
  },
  {
    key: 'description',
    label: 'Description',
    defaultVisible: false,
    cellClassName: 'max-w-[220px] truncate text-xs text-muted-foreground',
    render: (item) => item.description ?? '—',
  },
  {
    key: 'country',
    label: 'Country',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => item.country ?? '—',
  },
  {
    key: 'hubspotContactLink',
    label: 'HubSpot contact',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) => {
      if (!item.hubspotContactLink)
        return <span className="text-muted-foreground">—</span>
      if (!/^https?:\/\//.test(item.hubspotContactLink)) {
        return (
          <span className="text-muted-foreground">
            {item.hubspotContactLink}
          </span>
        )
      }
      return (
        <a
          href={item.hubspotContactLink}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          View contact
          <ExternalLink className="size-3.5" />
        </a>
      )
    },
  },
  {
    key: 'coupon',
    label: 'Coupon',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => item.coupon ?? '—',
  },
  {
    key: 'currency',
    label: 'Currency',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => item.currency?.toUpperCase() ?? '—',
  },
  {
    key: 'delinquent',
    label: 'Delinquent',
    defaultVisible: false,
    render: (item) =>
      item.delinquent ? (
        <Badge
          variant="outline"
          className="border-red-500/30 bg-red-500/10 text-xs text-red-700 dark:text-red-400"
        >
          Delinquent
        </Badge>
      ) : (
        <span className="text-xs text-muted-foreground">No</span>
      ),
  },
  {
    key: 'synced',
    label: 'Synced',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => formatDateTime(item.lastSyncedAt),
  },
  {
    key: 'payments',
    label: 'Payments',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) => item.paymentsCount,
  },
  {
    key: 'firstPayment',
    label: 'First payment',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => formatDateTime(item.firstPaymentAt),
  },
  {
    key: 'lastPayment',
    label: 'Last payment',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => formatDateTime(item.lastPaymentAt),
  },
  {
    key: 'refundedVolume',
    label: 'Refunded volume',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) =>
      formatCurrency(item.refundedVolume, item.currency ?? 'USD'),
  },
]

function getDefaultCustomerColumnVisibility(): Record<
  CustomerColumnKey,
  boolean
> {
  return CUSTOMER_COLUMNS.reduce(
    (acc, column) => {
      acc[column.key] = column.defaultVisible
      return acc
    },
    {} as Record<CustomerColumnKey, boolean>,
  )
}

function loadCustomerColumnVisibility(): Record<CustomerColumnKey, boolean> {
  const defaults = getDefaultCustomerColumnVisibility()
  if (typeof window === 'undefined') return defaults
  try {
    const raw = window.localStorage.getItem(
      CUSTOMER_COLUMN_VISIBILITY_STORAGE_KEY,
    )
    if (!raw) return defaults
    const parsed = JSON.parse(raw) as Partial<
      Record<CustomerColumnKey, boolean>
    >
    return { ...defaults, ...parsed }
  } catch {
    return defaults
  }
}

function useCustomerColumnVisibility() {
  const [visibility, setVisibility] = React.useState<
    Partial<Record<CustomerColumnKey, boolean>>
  >(() => loadCustomerColumnVisibility())

  React.useEffect(() => {
    try {
      window.localStorage.setItem(
        CUSTOMER_COLUMN_VISIBILITY_STORAGE_KEY,
        JSON.stringify(visibility),
      )
    } catch {
      // Ignore storage errors (private browsing, quota, disabled storage, etc.)
    }
  }, [visibility])

  return [visibility, setVisibility] as const
}

function CustomerColumnsPicker({
  visibility,
  onVisibilityChange,
}: {
  visibility: Partial<Record<CustomerColumnKey, boolean>>
  onVisibilityChange: React.Dispatch<
    React.SetStateAction<Partial<Record<CustomerColumnKey, boolean>>>
  >
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
        >
          <Columns3 className="size-3.5" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">
          Toggle columns
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {CUSTOMER_COLUMNS.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.key}
            checked={visibility[column.key] ?? column.defaultVisible}
            onCheckedChange={(checked) =>
              onVisibilityChange((current) => ({
                ...current,
                [column.key]: checked,
              }))
            }
            onSelect={(event) => event.preventDefault()}
            className="text-xs"
          >
            {column.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function CustomersFilters({
  filters,
  onFiltersChange,
  billingPlans,
  columnVisibility,
  onColumnVisibilityChange,
}: {
  filters: StripeRecordsFilters
  onFiltersChange: React.Dispatch<React.SetStateAction<StripeRecordsFilters>>
  billingPlans: Array<StripeCustomerBillingPlan>
  columnVisibility: Partial<Record<CustomerColumnKey, boolean>>
  onColumnVisibilityChange: React.Dispatch<
    React.SetStateAction<Partial<Record<CustomerColumnKey, boolean>>>
  >
}) {
  return (
    <Card className="border-border/60">
      <CardContent className="flex flex-col gap-3 pt-6 lg:flex-row lg:items-center">
        <Input
          value={filters.search ?? ''}
          onChange={(event) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              search: event.target.value || undefined,
            }))
          }
          placeholder="Search Stripe customer id, name, or email"
          className="lg:max-w-sm"
        />
        <Select
          value={filters.billingPlanId ?? 'all'}
          onValueChange={(value) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              billingPlanId: value === 'all' ? undefined : value,
            }))
          }
        >
          <SelectTrigger className="h-8 w-full text-xs lg:w-48">
            <SelectValue placeholder="All plans" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            {billingPlans.map((plan) => (
              <SelectItem key={plan.id} value={plan.id}>
                {plan.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.sort ?? 'created_desc'}
          onValueChange={(value) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              sort: value as StripeRecordsFilters['sort'],
            }))
          }
        >
          <SelectTrigger className="h-8 w-full text-xs lg:w-44">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            {STRIPE_RECORDS_SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch
            id="stripe-customers-issues-only"
            size="sm"
            checked={filters.issuesOnly ?? false}
            onCheckedChange={(checked) =>
              onFiltersChange((current) => ({
                ...current,
                page: 1,
                issuesOnly: checked || undefined,
              }))
            }
          />
          <Label
            htmlFor="stripe-customers-issues-only"
            className="text-xs font-normal text-muted-foreground"
          >
            Issues only
          </Label>
        </div>
        <div className="flex items-center gap-2 lg:ml-auto">
          <CustomerColumnsPicker
            visibility={columnVisibility}
            onVisibilityChange={onColumnVisibilityChange}
          />
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            onClick={() =>
              onFiltersChange((current) => ({
                page: 1,
                perPage: current.perPage ?? PER_PAGE,
                clientId: current.clientId,
              }))
            }
          >
            Clear filters
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function CustomersSection({
  initialClientId,
  initialSearch,
}: {
  initialClientId?: string
  initialSearch?: string
}) {
  const [filters, setFilters] = React.useState<StripeRecordsFilters>({
    page: 1,
    perPage: PER_PAGE,
    clientId: initialClientId,
    search: initialSearch,
  })
  const { data, isLoading } = useStripeCustomers(filters)
  const [columnVisibility, setColumnVisibility] = useCustomerColumnVisibility()

  const visibleColumns = CUSTOMER_COLUMNS.filter(
    (column) => columnVisibility[column.key] ?? column.defaultVisible,
  )
  // Always-visible columns: Stripe customer, Client, Linkage, Actions.
  const columnCount = visibleColumns.length + 4

  return (
    <div className="space-y-4">
      <SyncToolbar
        object="customer"
        title="Customer sync"
        description="Refresh Stripe customer snapshots and maintain the customer rail used by checkout, invoices, and subscriptions."
      />
      <SyncToolbar
        object="payment_method"
        title="Payment method sync"
        description="Re-reads each client's attached cards and bank accounts from Stripe; records new ones and retires ones Stripe detached, moving billing to another method if the retired one was the default."
      />
      <CustomersFilters
        filters={filters}
        onFiltersChange={setFilters}
        billingPlans={data?.billingPlans ?? []}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
      />
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Stripe customer</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Linkage</TableHead>
              {visibleColumns.map((column) => (
                <TableHead key={column.key}>{column.label}</TableHead>
              ))}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <EmptyRows
                label="Loading Stripe customers…"
                colSpan={columnCount}
              />
            ) : null}
            {!isLoading && !data?.items.length ? (
              <EmptyRows
                label="No Stripe customers found for these filters."
                colSpan={columnCount}
              />
            ) : null}
            {data?.items.map((item: StripeCustomerRecord) => (
              <TableRow key={item.id}>
                <TableCell className="align-top">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <span
                        className="max-w-[150px] truncate font-mono text-xs"
                        title={item.stripeCustomerId}
                      >
                        {item.stripeCustomerId}
                      </span>
                      <CopyButton
                        content={item.stripeCustomerId}
                        size="xs"
                        variant="ghost"
                        aria-label="Copy Stripe customer id"
                      />
                    </div>
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs',
                        environmentBadgeClass(item.environment),
                      )}
                    >
                      {item.environment}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell className="align-top">
                  <ClientLinkCell client={item.client} />
                </TableCell>
                <TableCell className="align-top">
                  <CustomerLinkageCell item={item} />
                </TableCell>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={cn('align-top', column.cellClassName)}
                  >
                    {column.render(item)}
                  </TableCell>
                ))}
                <TableCell className="align-top text-right">
                  <div className="flex justify-end">
                    <CustomerActionsCell item={item} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <PaginationFooter
          page={data?.page ?? 1}
          totalPages={data?.totalPages ?? 1}
          total={data?.total ?? 0}
          isLoading={isLoading}
          onPageChange={(page) =>
            setFilters((current) => ({ ...current, page }))
          }
        />
      </Card>
    </div>
  )
}

type PaymentColumnKey =
  | 'method'
  | 'plan'
  | 'processed'
  | 'succeeded'
  | 'failed'
  | 'synced'
  | 'currency'
  | 'failureCode'
  | 'failureMessage'
  | 'paymentIntent'
  | 'charge'
  | 'customer'
  | 'invoice'
  | 'subscription'
  | 'checkoutSession'
  | 'envLivemode'

type PaymentColumnConfig = {
  key: PaymentColumnKey
  label: string
  defaultVisible: boolean
  headClassName?: string
  cellClassName?: string
  render: (item: StripePaymentRecord) => React.ReactNode
}

const PAYMENT_COLUMN_VISIBILITY_STORAGE_KEY =
  'stripe-payments-column-visibility'

const PAYMENT_COLUMNS: Array<PaymentColumnConfig> = [
  {
    key: 'method',
    label: 'Method',
    defaultVisible: true,
    cellClassName: 'text-xs',
    render: (item) =>
      paymentMethodLabel(item.paymentMethodSummary, item.paymentMethodType),
  },
  {
    key: 'plan',
    label: 'Plan',
    defaultVisible: true,
    cellClassName: 'text-xs',
    render: (item) => item.planLabel ?? '—',
  },
  {
    key: 'processed',
    label: 'Processed',
    defaultVisible: true,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => formatDateTime(item.processedAt),
  },
  {
    key: 'succeeded',
    label: 'Succeeded',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => formatDateTime(item.succeededAt),
  },
  {
    key: 'failed',
    label: 'Failed',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => formatDateTime(item.failedAt),
  },
  {
    key: 'synced',
    label: 'Synced',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => formatDateTime(item.lastSyncedAt),
  },
  {
    key: 'currency',
    label: 'Currency',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => item.currency?.toUpperCase() ?? '—',
  },
  {
    key: 'failureCode',
    label: 'Failure code',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) =>
      [
        item.failureCode,
        item.failureDeclineCode,
        item.failureNetworkDeclineCode,
      ]
        .filter(Boolean)
        .join(' · ') || '—',
  },
  {
    key: 'failureMessage',
    label: 'Failure message',
    defaultVisible: false,
    cellClassName: 'max-w-[220px] truncate text-xs',
    render: (item) =>
      item.failureMessage ? (
        <span className="text-destructive">{item.failureMessage}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'paymentIntent',
    label: 'Payment intent',
    defaultVisible: false,
    cellClassName: 'font-mono text-xs text-muted-foreground',
    render: (item) => item.stripePaymentIntentId,
  },
  {
    key: 'charge',
    label: 'Charge',
    defaultVisible: false,
    cellClassName: 'font-mono text-xs text-muted-foreground',
    render: (item) => item.stripeChargeId ?? '—',
  },
  {
    key: 'customer',
    label: 'Stripe customer',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) =>
      item.stripeCustomer || item.stripeCustomerId ? (
        <div className="space-y-0.5">
          {item.stripeCustomer?.name || item.stripeCustomer?.email ? (
            <p className="font-medium text-foreground">
              {item.stripeCustomer.name ?? item.stripeCustomer.email}
            </p>
          ) : null}
          <p className="font-mono text-muted-foreground">
            {item.stripeCustomerId ?? '—'}
          </p>
        </div>
      ) : (
        '—'
      ),
  },
  {
    key: 'invoice',
    label: 'Invoice',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) =>
      item.stripeInvoiceId ? (
        <a
          href={stripeObjectUrl('invoice', item.stripeInvoiceId, item.livemode)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          Invoice
          <ExternalLink className="size-3.5" />
        </a>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'subscription',
    label: 'Subscription',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) =>
      item.stripeSubscriptionId ? (
        <a
          href={stripeObjectUrl(
            'subscription',
            item.stripeSubscriptionId,
            item.livemode,
          )}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          Subscription
          <ExternalLink className="size-3.5" />
        </a>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'checkoutSession',
    label: 'Checkout session',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) =>
      item.checkoutIntent?.checkoutUrl ? (
        <a
          href={item.checkoutIntent.checkoutUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          Checkout intent
          <ExternalLink className="size-3.5" />
        </a>
      ) : (
        <span className="font-mono text-muted-foreground">
          {item.stripeCheckoutSessionId ?? '—'}
        </span>
      ),
  },
  {
    key: 'envLivemode',
    label: 'Env/livemode',
    defaultVisible: false,
    render: (item) => (
      <div className="flex flex-wrap gap-1">
        <Badge
          variant="outline"
          className={cn(
            'text-xs',
            environmentBadgeClass(item.stripeEnvironment),
          )}
        >
          {item.stripeEnvironment}
        </Badge>
        <Badge
          variant="outline"
          className={cn('text-xs', livemodeBadgeClass(item.livemode))}
        >
          {item.livemode ? 'Live' : 'Test'}
        </Badge>
      </div>
    ),
  },
]

function getDefaultPaymentColumnVisibility(): Record<
  PaymentColumnKey,
  boolean
> {
  return PAYMENT_COLUMNS.reduce(
    (acc, column) => {
      acc[column.key] = column.defaultVisible
      return acc
    },
    {} as Record<PaymentColumnKey, boolean>,
  )
}

function loadPaymentColumnVisibility(): Record<PaymentColumnKey, boolean> {
  const defaults = getDefaultPaymentColumnVisibility()
  if (typeof window === 'undefined') return defaults
  try {
    const raw = window.localStorage.getItem(
      PAYMENT_COLUMN_VISIBILITY_STORAGE_KEY,
    )
    if (!raw) return defaults
    const parsed = JSON.parse(raw) as Partial<Record<PaymentColumnKey, boolean>>
    return { ...defaults, ...parsed }
  } catch {
    return defaults
  }
}

function usePaymentColumnVisibility() {
  const [visibility, setVisibility] = React.useState<
    Partial<Record<PaymentColumnKey, boolean>>
  >(() => loadPaymentColumnVisibility())

  React.useEffect(() => {
    try {
      window.localStorage.setItem(
        PAYMENT_COLUMN_VISIBILITY_STORAGE_KEY,
        JSON.stringify(visibility),
      )
    } catch {
      // Ignore storage errors (private browsing, quota, disabled storage, etc.)
    }
  }, [visibility])

  return [visibility, setVisibility] as const
}

function PaymentColumnsPicker({
  visibility,
  onVisibilityChange,
}: {
  visibility: Partial<Record<PaymentColumnKey, boolean>>
  onVisibilityChange: React.Dispatch<
    React.SetStateAction<Partial<Record<PaymentColumnKey, boolean>>>
  >
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
        >
          <Columns3 className="size-3.5" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">
          Toggle columns
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {PAYMENT_COLUMNS.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.key}
            checked={visibility[column.key] ?? column.defaultVisible}
            onCheckedChange={(checked) =>
              onVisibilityChange((current) => ({
                ...current,
                [column.key]: checked,
              }))
            }
            onSelect={(event) => event.preventDefault()}
            className="text-xs"
          >
            {column.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function PaymentsToolbar({
  filters,
  onFiltersChange,
  billingPlans,
  columnVisibility,
  onColumnVisibilityChange,
  statuses,
}: {
  filters: StripeRecordsFilters
  onFiltersChange: React.Dispatch<React.SetStateAction<StripeRecordsFilters>>
  billingPlans: Array<StripeCustomerBillingPlan>
  statuses: Array<string>
  columnVisibility: Partial<Record<PaymentColumnKey, boolean>>
  onColumnVisibilityChange: React.Dispatch<
    React.SetStateAction<Partial<Record<PaymentColumnKey, boolean>>>
  >
}) {
  return (
    <Card className="border-border/60">
      <CardContent className="flex flex-col gap-3 pt-6 lg:flex-row lg:items-center">
        <Input
          value={filters.search ?? ''}
          onChange={(event) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              search: event.target.value || undefined,
            }))
          }
          placeholder="Search Stripe id, customer, client, or linked record"
          className="lg:max-w-sm"
        />
        <Select
          value={filters.status ?? 'all'}
          onValueChange={(value) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              status: value === 'all' ? undefined : value,
            }))
          }
        >
          <SelectTrigger className="h-8 w-full text-xs lg:w-52">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((status) => (
              <SelectItem key={status} value={status}>
                {paymentStatusLabel(status)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.billingPlanId ?? 'all'}
          onValueChange={(value) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              billingPlanId: value === 'all' ? undefined : value,
            }))
          }
        >
          <SelectTrigger className="h-8 w-full text-xs lg:w-52">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            {billingPlans.map((plan) => (
              <SelectItem key={plan.id} value={plan.id}>
                {plan.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.sort ?? 'created_desc'}
          onValueChange={(value) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              sort: value as StripeRecordsFilters['sort'],
            }))
          }
        >
          <SelectTrigger className="h-8 w-full text-xs lg:w-44">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            {STRIPE_RECORDS_SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 lg:ml-auto">
          <PaymentColumnsPicker
            visibility={columnVisibility}
            onVisibilityChange={onColumnVisibilityChange}
          />
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            onClick={() =>
              onFiltersChange((current) => ({
                page: 1,
                perPage: current.perPage ?? PER_PAGE,
                clientId: current.clientId,
              }))
            }
          >
            Clear
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Sets the CUSTOMER's default payment method from a payment row — e.g. "this
 * card failed, switch the default to the one on file that works." Does not
 * touch the specific PaymentIntent, which is already settled; it only changes
 * what future charges use.
 */
function PaymentRowChangeMethodAction({ item }: { item: StripePaymentRecord }) {
  const setDefault = useSetDefaultPaymentMethod()
  const billingProfileId = item.billingProfile?.id ?? null

  return (
    <PaymentMethodPickerMenu
      clientBillingProfileId={billingProfileId}
      isPending={setDefault.isPending}
      disabledReason="No billing profile linked to this customer"
      onSelect={(stripePaymentMethodId) =>
        setDefault.mutate({
          stripePaymentMethodId,
          clientBillingProfileId: billingProfileId!,
        })
      }
    />
  )
}

function PaymentsSection({ initialClientId }: { initialClientId?: string }) {
  const [filters, setFilters] = React.useState<StripeRecordsFilters>({
    page: 1,
    perPage: PER_PAGE,
    clientId: initialClientId,
  })
  const { data, isLoading } = useStripePayments(filters)
  const [columnVisibility, setColumnVisibility] = usePaymentColumnVisibility()

  const visibleColumns = PAYMENT_COLUMNS.filter(
    (column) => columnVisibility[column.key] ?? column.defaultVisible,
  )
  // Always-visible columns: Status, Amount, Client, Actions.
  const columnCount = visibleColumns.length + 4

  return (
    <div className="space-y-4">
      <SyncToolbar
        object="payment"
        title="Payment sync"
        description="Mirror Stripe payment intent and charge activity so backoffice can review card and ACH outcomes, stored payment method details, and links back to checkout, invoices, and the client billing profile."
      />
      <PaymentsToolbar
        filters={filters}
        onFiltersChange={setFilters}
        billingPlans={data?.billingPlans ?? []}
        statuses={data?.statuses ?? []}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
      />
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Client</TableHead>
              {visibleColumns.map((column) => (
                <TableHead key={column.key}>{column.label}</TableHead>
              ))}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <EmptyRows
                label="Loading Stripe payments…"
                colSpan={columnCount}
              />
            ) : null}
            {!isLoading && !data?.items.length ? (
              <EmptyRows
                label="No Stripe payment mirrors found for these filters."
                colSpan={columnCount}
              />
            ) : null}
            {data?.items.map((item: StripePaymentRecord) => (
              <TableRow key={item.id}>
                <TableCell className="align-top">
                  <div className="space-y-1">
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs',
                        livemodeBadgeClass(item.livemode),
                      )}
                    >
                      {item.livemode ? 'Live' : 'Test'}
                    </Badge>
                    {isBookkeepingCancel(item) ? (
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-muted-foreground">
                          {paymentStatusLabel(item.status)}
                        </p>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="secondary" className="text-xs">
                                Bookkeeping
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              Stripe auto-canceled the invoice&apos;s duplicate
                              receipt PaymentIntent when the real payment was
                              attached — not a customer cancellation.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    ) : (
                      <p
                        className={cn(
                          'font-medium',
                          isFailedPaymentStatus(item.status) &&
                            'text-destructive',
                        )}
                      >
                        {paymentStatusLabel(item.status)}
                      </p>
                    )}
                    <p className="font-mono text-xs text-muted-foreground">
                      {item.stripePaymentIntentId}
                    </p>
                  </div>
                </TableCell>
                <TableCell className="align-top">
                  <p className="font-medium">
                    {formatCurrency(item.amount, item.currency ?? 'USD')}
                  </p>
                </TableCell>
                <TableCell className="align-top">
                  <div className="space-y-0.5">
                    {item.client ? (
                      <ClientLinkCell client={item.client} />
                    ) : item.stripeCustomer &&
                      (item.stripeCustomer.name ||
                        item.stripeCustomer.email) ? (
                      <>
                        <p className="font-medium">
                          {item.stripeCustomer.name ??
                            item.stripeCustomer.email}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {item.stripeCustomer.name && item.stripeCustomer.email
                            ? item.stripeCustomer.email
                            : 'Stripe customer · no Rothology client'}
                        </p>
                      </>
                    ) : !item.stripeCustomerId ? (
                      <span className="text-xs text-muted-foreground">
                        No customer linked
                      </span>
                    ) : null}
                    {item.stripeCustomerId ? (
                      <Link
                        to="/backoffice/integrations/stripe"
                        search={{
                          tab: 'customers',
                          customerSearch: item.stripeCustomerId,
                        }}
                        title="View on the Customers tab"
                        className="block font-mono text-[11px] text-muted-foreground hover:text-primary hover:underline"
                      >
                        {item.stripeCustomerId}
                      </Link>
                    ) : null}
                  </div>
                </TableCell>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={cn('align-top', column.cellClassName)}
                  >
                    {column.render(item)}
                  </TableCell>
                ))}
                <TableCell className="align-top text-right">
                  <div className="flex justify-end">
                    <PaymentRowChangeMethodAction item={item} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <PaginationFooter
          page={data?.page ?? 1}
          totalPages={data?.totalPages ?? 1}
          total={data?.total ?? 0}
          isLoading={isLoading}
          onPageChange={(page) =>
            setFilters((current) => ({ ...current, page }))
          }
        />
      </Card>
    </div>
  )
}

type InvoiceColumnKey =
  | 'plan'
  | 'dates'
  | 'links'
  | 'envLivemode'
  | 'collectionMethod'
  | 'billingStatus'
  | 'currency'
  | 'stripeCustomer'
  | 'subscription'
  | 'paymentIntent'
  | 'checkoutSession'

type InvoiceColumnConfig = {
  key: InvoiceColumnKey
  label: string
  defaultVisible: boolean
  headClassName?: string
  cellClassName?: string
  render: (item: StripeInvoiceRecord) => React.ReactNode
}

const INVOICE_COLUMN_VISIBILITY_STORAGE_KEY =
  'stripe-invoices-column-visibility'

const INVOICE_COLUMNS: Array<InvoiceColumnConfig> = [
  {
    key: 'plan',
    label: 'Plan',
    defaultVisible: true,
    cellClassName: 'text-xs',
    render: (item) => item.planLabel ?? '—',
  },
  {
    key: 'dates',
    label: 'Dates',
    defaultVisible: true,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => (
      <>
        <p>Due {formatDateTime(item.dueDate)}</p>
        <p>Paid {formatDateTime(item.paidAt)}</p>
        <p>Synced {formatDateTime(item.lastSyncedAt)}</p>
      </>
    ),
  },
  {
    key: 'links',
    label: 'Links',
    defaultVisible: true,
    cellClassName: 'text-xs',
    render: (item) => (
      <div className="flex flex-col items-start gap-2">
        {item.hostedInvoiceUrl ? (
          <a
            href={item.hostedInvoiceUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            Hosted invoice
            <ExternalLink className="size-3.5" />
          </a>
        ) : null}
        {item.invoicePdfUrl ? (
          <a
            href={item.invoicePdfUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            PDF
            <ExternalLink className="size-3.5" />
          </a>
        ) : null}
        {item.checkoutIntent?.checkoutUrl ? (
          <a
            href={item.checkoutIntent.checkoutUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            Checkout intent
            <ExternalLink className="size-3.5" />
          </a>
        ) : null}
        {!item.hostedInvoiceUrl &&
        !item.invoicePdfUrl &&
        !item.checkoutIntent?.checkoutUrl ? (
          <span className="text-muted-foreground">—</span>
        ) : null}
      </div>
    ),
  },
  {
    key: 'envLivemode',
    label: 'Env/livemode',
    defaultVisible: false,
    render: (item) => (
      <div className="flex flex-wrap gap-1">
        <Badge
          variant="outline"
          className={cn(
            'text-xs',
            environmentBadgeClass(item.stripeEnvironment),
          )}
        >
          {item.stripeEnvironment}
        </Badge>
        <Badge
          variant="outline"
          className={cn('text-xs', livemodeBadgeClass(item.livemode))}
        >
          {item.livemode ? 'Live' : 'Test'}
        </Badge>
      </div>
    ),
  },
  {
    key: 'collectionMethod',
    label: 'Collection method',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => item.collectionMethod?.replaceAll('_', ' ') ?? '—',
  },
  {
    key: 'billingStatus',
    label: 'Billing status',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => item.billingStatus ?? '—',
  },
  {
    key: 'currency',
    label: 'Currency',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => item.currency?.toUpperCase() ?? '—',
  },
  {
    key: 'stripeCustomer',
    label: 'Stripe customer',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) =>
      item.stripeCustomer || item.stripeCustomerId ? (
        <div className="space-y-0.5">
          {item.stripeCustomer?.name || item.stripeCustomer?.email ? (
            <p className="font-medium text-foreground">
              {item.stripeCustomer.name ?? item.stripeCustomer.email}
            </p>
          ) : null}
          <p className="font-mono text-muted-foreground">
            {item.stripeCustomerId ?? '—'}
          </p>
        </div>
      ) : (
        '—'
      ),
  },
  {
    key: 'subscription',
    label: 'Subscription',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) =>
      item.stripeSubscriptionId ? (
        <a
          href={stripeObjectUrl(
            'subscription',
            item.stripeSubscriptionId,
            item.livemode,
          )}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          Subscription
          <ExternalLink className="size-3.5" />
        </a>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'paymentIntent',
    label: 'Payment intent',
    defaultVisible: false,
    cellClassName: 'font-mono text-xs text-muted-foreground',
    render: (item) => item.stripePaymentIntentId ?? '—',
  },
  {
    key: 'checkoutSession',
    label: 'Checkout session',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) =>
      item.checkoutIntent?.checkoutUrl ? (
        <a
          href={item.checkoutIntent.checkoutUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          Checkout intent
          <ExternalLink className="size-3.5" />
        </a>
      ) : (
        <span className="font-mono text-muted-foreground">
          {item.stripeCheckoutSessionId ?? '—'}
        </span>
      ),
  },
]

function getDefaultInvoiceColumnVisibility(): Record<
  InvoiceColumnKey,
  boolean
> {
  return INVOICE_COLUMNS.reduce(
    (acc, column) => {
      acc[column.key] = column.defaultVisible
      return acc
    },
    {} as Record<InvoiceColumnKey, boolean>,
  )
}

function loadInvoiceColumnVisibility(): Record<InvoiceColumnKey, boolean> {
  const defaults = getDefaultInvoiceColumnVisibility()
  if (typeof window === 'undefined') return defaults
  try {
    const raw = window.localStorage.getItem(
      INVOICE_COLUMN_VISIBILITY_STORAGE_KEY,
    )
    if (!raw) return defaults
    const parsed = JSON.parse(raw) as Partial<Record<InvoiceColumnKey, boolean>>
    return { ...defaults, ...parsed }
  } catch {
    return defaults
  }
}

function useInvoiceColumnVisibility() {
  const [visibility, setVisibility] = React.useState<
    Partial<Record<InvoiceColumnKey, boolean>>
  >(() => loadInvoiceColumnVisibility())

  React.useEffect(() => {
    try {
      window.localStorage.setItem(
        INVOICE_COLUMN_VISIBILITY_STORAGE_KEY,
        JSON.stringify(visibility),
      )
    } catch {
      // Ignore storage errors (private browsing, quota, disabled storage, etc.)
    }
  }, [visibility])

  return [visibility, setVisibility] as const
}

function InvoiceColumnsPicker({
  visibility,
  onVisibilityChange,
}: {
  visibility: Partial<Record<InvoiceColumnKey, boolean>>
  onVisibilityChange: React.Dispatch<
    React.SetStateAction<Partial<Record<InvoiceColumnKey, boolean>>>
  >
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
        >
          <Columns3 className="size-3.5" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">
          Toggle columns
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {INVOICE_COLUMNS.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.key}
            checked={visibility[column.key] ?? column.defaultVisible}
            onCheckedChange={(checked) =>
              onVisibilityChange((current) => ({
                ...current,
                [column.key]: checked,
              }))
            }
            onSelect={(event) => event.preventDefault()}
            className="text-xs"
          >
            {column.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function InvoicesToolbar({
  filters,
  onFiltersChange,
  billingPlans,
  statuses,
  columnVisibility,
  onColumnVisibilityChange,
}: {
  filters: StripeRecordsFilters
  onFiltersChange: React.Dispatch<React.SetStateAction<StripeRecordsFilters>>
  billingPlans: Array<StripeCustomerBillingPlan>
  statuses: Array<string>
  columnVisibility: Partial<Record<InvoiceColumnKey, boolean>>
  onColumnVisibilityChange: React.Dispatch<
    React.SetStateAction<Partial<Record<InvoiceColumnKey, boolean>>>
  >
}) {
  return (
    <Card className="border-border/60">
      <CardContent className="flex flex-col gap-3 pt-6 lg:flex-row lg:items-center">
        <Input
          value={filters.search ?? ''}
          onChange={(event) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              search: event.target.value || undefined,
            }))
          }
          placeholder="Search Stripe id, customer, client, or linked record"
          className="lg:max-w-sm"
        />
        <Select
          value={filters.status ?? 'all'}
          onValueChange={(value) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              status: value === 'all' ? undefined : value,
            }))
          }
        >
          <SelectTrigger className="h-8 w-full text-xs lg:w-52">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((status) => (
              <SelectItem key={status} value={status}>
                {status.replaceAll('_', ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.billingPlanId ?? 'all'}
          onValueChange={(value) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              billingPlanId: value === 'all' ? undefined : value,
            }))
          }
        >
          <SelectTrigger className="h-8 w-full text-xs lg:w-52">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            {billingPlans.map((plan) => (
              <SelectItem key={plan.id} value={plan.id}>
                {plan.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.sort ?? 'created_desc'}
          onValueChange={(value) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              sort: value as StripeRecordsFilters['sort'],
            }))
          }
        >
          <SelectTrigger className="h-8 w-full text-xs lg:w-44">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            {STRIPE_RECORDS_SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 lg:ml-auto">
          <InvoiceColumnsPicker
            visibility={columnVisibility}
            onVisibilityChange={onColumnVisibilityChange}
          />
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            onClick={() =>
              onFiltersChange((current) => ({
                page: 1,
                perPage: current.perPage ?? PER_PAGE,
                clientId: current.clientId,
              }))
            }
          >
            Clear
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function LinkedInvoiceCell({ item }: { item: StripeInvoiceRecord }) {
  const unlink = useLinkStripeInvoiceHubspot()

  if (
    item.reconciliationStatus === 'LINKED_TO_HUBSPOT' &&
    item.hubspotInvoice
  ) {
    const hubspotInvoice = item.hubspotInvoice
    return (
      <div className="space-y-1.5">
        <Link
          to="/backoffice/integrations/hubspot"
          search={{ tab: 'records', recordType: 'invoices' }}
          className="block w-fit rounded-md border border-border/60 px-2 py-1 text-xs hover:border-primary hover:bg-muted/50"
        >
          <p className="font-medium text-foreground">
            {hubspotInvoice.hsInvoiceNumber ?? hubspotInvoice.hubspotInvoiceId}
          </p>
          <Badge variant="outline" className="mt-1 text-xs">
            {hubspotInvoice.status}
          </Badge>
        </Link>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={unlink.isPending}
          className="h-6 gap-1 px-1.5 text-xs text-muted-foreground hover:text-destructive"
          onClick={() => unlink.mutate({ id: item.id, hubspotInvoiceId: null })}
        >
          <Link2Off className="size-3.5" />
          Unlink
        </Button>
      </div>
    )
  }

  return (
    <HubspotInvoicePicker stripeInvoiceRowId={item.id} client={item.client} />
  )
}

function InvoicesSection({ initialClientId }: { initialClientId?: string }) {
  const [filters, setFilters] = React.useState<StripeRecordsFilters>({
    page: 1,
    perPage: PER_PAGE,
    clientId: initialClientId,
  })
  const { data, isLoading } = useStripeInvoices(filters)
  const [columnVisibility, setColumnVisibility] = useInvoiceColumnVisibility()

  const visibleColumns = INVOICE_COLUMNS.filter(
    (column) => columnVisibility[column.key] ?? column.defaultVisible,
  )
  // Always-visible columns: Invoice, Linked Invoice, Amounts, Client.
  const columnCount = visibleColumns.length + 4

  return (
    <div className="space-y-4">
      <SyncToolbar
        object="invoice"
        title="Invoice sync"
        description="Mirror Stripe invoices, reconcile them to HubSpot using the shared Stripe invoice id, and treat the Stripe mirror as the primary invoice rail whenever both systems hold the same billing record."
      />
      <InvoicesToolbar
        filters={filters}
        onFiltersChange={setFilters}
        billingPlans={data?.billingPlans ?? []}
        statuses={data?.statuses ?? []}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
      />
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead>Linked Invoice</TableHead>
              <TableHead>Amounts</TableHead>
              <TableHead>Client</TableHead>
              {visibleColumns.map((column) => (
                <TableHead key={column.key}>{column.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <EmptyRows
                label="Loading Stripe invoices…"
                colSpan={columnCount}
              />
            ) : null}
            {!isLoading && !data?.items.length ? (
              <EmptyRows
                label="No Stripe invoice mirrors found for these filters."
                colSpan={columnCount}
              />
            ) : null}
            {data?.items.map((item: StripeInvoiceRecord) => (
              <TableRow key={item.id}>
                <TableCell className="align-top">
                  <div className="space-y-1">
                    <p className="font-medium">
                      {item.stripeInvoiceNumber ?? item.stripeInvoiceId}
                    </p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {item.stripeInvoiceId}
                    </p>
                    <Badge variant="outline" className="text-xs">
                      {item.status}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell className="align-top">
                  <LinkedInvoiceCell item={item} />
                </TableCell>
                <TableCell className="align-top">
                  <div className="space-y-1 text-xs">
                    <p>
                      Due:{' '}
                      <span className="font-medium text-foreground">
                        {formatCurrency(item.amountDue, item.currency ?? 'USD')}
                      </span>
                    </p>
                    <p>
                      Paid:{' '}
                      <span className="font-medium text-foreground">
                        {formatCurrency(
                          item.amountPaid,
                          item.currency ?? 'USD',
                        )}
                      </span>
                    </p>
                    <p>
                      Remaining:{' '}
                      <span className="font-medium text-foreground">
                        {formatCurrency(
                          item.amountRemaining,
                          item.currency ?? 'USD',
                        )}
                      </span>
                    </p>
                  </div>
                </TableCell>
                <TableCell className="align-top">
                  <div className="space-y-0.5">
                    {item.client ? (
                      <ClientLinkCell client={item.client} />
                    ) : item.stripeCustomer &&
                      (item.stripeCustomer.name ||
                        item.stripeCustomer.email) ? (
                      <>
                        <p className="font-medium">
                          {item.stripeCustomer.name ??
                            item.stripeCustomer.email}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {item.stripeCustomer.name && item.stripeCustomer.email
                            ? item.stripeCustomer.email
                            : 'Stripe customer · no Rothology client'}
                        </p>
                      </>
                    ) : !item.stripeCustomerId ? (
                      <span className="text-xs text-muted-foreground">
                        No customer linked
                      </span>
                    ) : null}
                    {item.stripeCustomerId ? (
                      <Link
                        to="/backoffice/integrations/stripe"
                        search={{
                          tab: 'customers',
                          customerSearch: item.stripeCustomerId,
                        }}
                        title="View on the Customers tab"
                        className="block font-mono text-[11px] text-muted-foreground hover:text-primary hover:underline"
                      >
                        {item.stripeCustomerId}
                      </Link>
                    ) : null}
                  </div>
                </TableCell>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={cn('align-top', column.cellClassName)}
                  >
                    {column.render(item)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <PaginationFooter
          page={data?.page ?? 1}
          totalPages={data?.totalPages ?? 1}
          total={data?.total ?? 0}
          isLoading={isLoading}
          onPageChange={(page) =>
            setFilters((current) => ({ ...current, page }))
          }
        />
      </Card>
    </div>
  )
}

type SubscriptionColumnKey =
  | 'plan'
  | 'currentPeriod'
  | 'paymentMethod'
  | 'envLivemode'
  | 'currency'
  | 'cancelAtPeriodEnd'
  | 'cancelAt'
  | 'canceledAt'
  | 'endedAt'
  | 'synced'
  | 'billingPlan'
  | 'latestInvoice'
  | 'checkoutSession'
  | 'stripeCustomer'

type SubscriptionColumnConfig = {
  key: SubscriptionColumnKey
  label: string
  defaultVisible: boolean
  headClassName?: string
  cellClassName?: string
  render: (item: StripeSubscriptionRecord) => React.ReactNode
}

const SUBSCRIPTION_COLUMN_VISIBILITY_STORAGE_KEY =
  'stripe-subscriptions-column-visibility'

const SUBSCRIPTION_COLUMNS: Array<SubscriptionColumnConfig> = [
  {
    key: 'plan',
    label: 'Plan',
    defaultVisible: true,
    cellClassName: 'text-xs',
    render: (item) => item.planLabel ?? '—',
  },
  {
    key: 'currentPeriod',
    label: 'Current period',
    defaultVisible: true,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => (
      <>
        <p>Start {formatDateTime(item.currentPeriodStart)}</p>
        <p>End {formatDateTime(item.currentPeriodEnd)}</p>
      </>
    ),
  },
  {
    key: 'paymentMethod',
    label: 'Payment method',
    defaultVisible: true,
    cellClassName: 'text-xs',
    render: (item) => paymentMethodLabel(item.paymentMethodSummary),
  },
  {
    key: 'envLivemode',
    label: 'Env/livemode',
    defaultVisible: false,
    render: (item) => (
      <div className="flex flex-wrap gap-1">
        <Badge
          variant="outline"
          className={cn(
            'text-xs',
            environmentBadgeClass(item.stripeEnvironment),
          )}
        >
          {item.stripeEnvironment}
        </Badge>
        <Badge
          variant="outline"
          className={cn('text-xs', livemodeBadgeClass(item.livemode))}
        >
          {item.livemode ? 'Live' : 'Test'}
        </Badge>
      </div>
    ),
  },
  {
    key: 'currency',
    label: 'Currency',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => item.currency?.toUpperCase() ?? '—',
  },
  {
    key: 'cancelAtPeriodEnd',
    label: 'Cancel at period end',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => (item.cancelAtPeriodEnd ? 'Yes' : 'No'),
  },
  {
    key: 'cancelAt',
    label: 'Cancel at',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => formatDateTime(item.cancelAt),
  },
  {
    key: 'canceledAt',
    label: 'Canceled at',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => formatDateTime(item.canceledAt),
  },
  {
    key: 'endedAt',
    label: 'Ended at',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => formatDateTime(item.endedAt),
  },
  {
    key: 'synced',
    label: 'Synced',
    defaultVisible: false,
    cellClassName: 'text-xs text-muted-foreground',
    render: (item) => formatDateTime(item.lastSyncedAt),
  },
  {
    key: 'billingPlan',
    label: 'Billing plan',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) => (
      <div className="space-y-0.5">
        <p className="font-medium text-foreground">
          {item.billingPlan?.experienceLabel ?? 'No billing plan linked'}
        </p>
        <p className="text-muted-foreground">{item.billingPlan?.name ?? '—'}</p>
      </div>
    ),
  },
  {
    key: 'latestInvoice',
    label: 'Latest invoice',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) =>
      item.latestStripeInvoiceId ? (
        <a
          href={stripeObjectUrl(
            'invoice',
            item.latestStripeInvoiceId,
            item.livemode,
          )}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          Latest invoice
          <ExternalLink className="size-3.5" />
        </a>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'checkoutSession',
    label: 'Checkout',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) =>
      item.checkoutIntent?.checkoutUrl ? (
        <a
          href={item.checkoutIntent.checkoutUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          Checkout intent
          <ExternalLink className="size-3.5" />
        </a>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'stripeCustomer',
    label: 'Stripe customer',
    defaultVisible: false,
    cellClassName: 'text-xs',
    render: (item) =>
      item.stripeCustomer || item.stripeCustomerId ? (
        <div className="space-y-0.5">
          {item.stripeCustomer?.name || item.stripeCustomer?.email ? (
            <p className="font-medium text-foreground">
              {item.stripeCustomer.name ?? item.stripeCustomer.email}
            </p>
          ) : null}
          <p className="font-mono text-muted-foreground">
            {item.stripeCustomerId ?? '—'}
          </p>
        </div>
      ) : (
        '—'
      ),
  },
]

function getDefaultSubscriptionColumnVisibility(): Record<
  SubscriptionColumnKey,
  boolean
> {
  return SUBSCRIPTION_COLUMNS.reduce(
    (acc, column) => {
      acc[column.key] = column.defaultVisible
      return acc
    },
    {} as Record<SubscriptionColumnKey, boolean>,
  )
}

function loadSubscriptionColumnVisibility(): Record<
  SubscriptionColumnKey,
  boolean
> {
  const defaults = getDefaultSubscriptionColumnVisibility()
  if (typeof window === 'undefined') return defaults
  try {
    const raw = window.localStorage.getItem(
      SUBSCRIPTION_COLUMN_VISIBILITY_STORAGE_KEY,
    )
    if (!raw) return defaults
    const parsed = JSON.parse(raw) as Partial<
      Record<SubscriptionColumnKey, boolean>
    >
    return { ...defaults, ...parsed }
  } catch {
    return defaults
  }
}

function useSubscriptionColumnVisibility() {
  const [visibility, setVisibility] = React.useState<
    Partial<Record<SubscriptionColumnKey, boolean>>
  >(() => loadSubscriptionColumnVisibility())

  React.useEffect(() => {
    try {
      window.localStorage.setItem(
        SUBSCRIPTION_COLUMN_VISIBILITY_STORAGE_KEY,
        JSON.stringify(visibility),
      )
    } catch {
      // Ignore storage errors (private browsing, quota, disabled storage, etc.)
    }
  }, [visibility])

  return [visibility, setVisibility] as const
}

function SubscriptionColumnsPicker({
  visibility,
  onVisibilityChange,
}: {
  visibility: Partial<Record<SubscriptionColumnKey, boolean>>
  onVisibilityChange: React.Dispatch<
    React.SetStateAction<Partial<Record<SubscriptionColumnKey, boolean>>>
  >
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
        >
          <Columns3 className="size-3.5" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">
          Toggle columns
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUBSCRIPTION_COLUMNS.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.key}
            checked={visibility[column.key] ?? column.defaultVisible}
            onCheckedChange={(checked) =>
              onVisibilityChange((current) => ({
                ...current,
                [column.key]: checked,
              }))
            }
            onSelect={(event) => event.preventDefault()}
            className="text-xs"
          >
            {column.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Let every live subscription bill either rail.
 *
 * A subscription's accepted rails are fixed when Stripe creates it, so every
 * subscription made before sc-1238 is pinned to whichever rail its checkout
 * used — and a customer who moves their default to a bank account cannot move
 * the subscription with it. This widens the ones already out there. Idempotent,
 * so it is safe to run again per environment.
 */
function WidenRailsMenuItem() {
  const widenRails = useWidenStripeSubscriptionRails()

  return (
    <DropdownMenuItem
      className="flex flex-col items-start gap-0.5 whitespace-normal py-2"
      disabled={widenRails.isPending}
      onSelect={() => widenRails.mutate()}
    >
      <span className="text-xs font-medium">
        {widenRails.isPending ? 'Widening rails…' : 'Widen payment rails'}
      </span>
      <span className="text-xs text-muted-foreground">
        Let every live subscription accept both a card and a bank account. Older
        ones are locked to the rail their checkout used.
      </span>
    </DropdownMenuItem>
  )
}

function SubscriptionsToolbar({
  filters,
  onFiltersChange,
  billingPlans,
  statuses,
  columnVisibility,
  onColumnVisibilityChange,
}: {
  filters: StripeRecordsFilters
  onFiltersChange: React.Dispatch<React.SetStateAction<StripeRecordsFilters>>
  billingPlans: Array<StripeCustomerBillingPlan>
  statuses: Array<string>
  columnVisibility: Partial<Record<SubscriptionColumnKey, boolean>>
  onColumnVisibilityChange: React.Dispatch<
    React.SetStateAction<Partial<Record<SubscriptionColumnKey, boolean>>>
  >
}) {
  return (
    <Card className="border-border/60">
      <CardContent className="flex flex-col gap-3 pt-6 lg:flex-row lg:items-center">
        <Input
          value={filters.search ?? ''}
          onChange={(event) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              search: event.target.value || undefined,
            }))
          }
          placeholder="Search Stripe id, customer, client, or linked record"
          className="lg:max-w-sm"
        />
        <Select
          value={filters.status ?? 'all'}
          onValueChange={(value) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              status: value === 'all' ? undefined : value,
            }))
          }
        >
          <SelectTrigger className="h-8 w-full text-xs lg:w-52">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((status) => (
              <SelectItem key={status} value={status}>
                {status.replaceAll('_', ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.billingPlanId ?? 'all'}
          onValueChange={(value) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              billingPlanId: value === 'all' ? undefined : value,
            }))
          }
        >
          <SelectTrigger className="h-8 w-full text-xs lg:w-52">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            {billingPlans.map((plan) => (
              <SelectItem key={plan.id} value={plan.id}>
                {plan.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.sort ?? 'created_desc'}
          onValueChange={(value) =>
            onFiltersChange((current) => ({
              ...current,
              page: 1,
              sort: value as StripeRecordsFilters['sort'],
            }))
          }
        >
          <SelectTrigger className="h-8 w-full text-xs lg:w-44">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            {STRIPE_RECORDS_SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 lg:ml-auto">
          <SubscriptionColumnsPicker
            visibility={columnVisibility}
            onVisibilityChange={onColumnVisibilityChange}
          />
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            onClick={() =>
              onFiltersChange((current) => ({
                page: 1,
                perPage: current.perPage ?? PER_PAGE,
                clientId: current.clientId,
              }))
            }
          >
            Clear
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Repoints one running subscription's future invoices at a different stored
 * method. Deliberately narrow: a subscription keeps its own default, so this
 * never touches the customer-level default the Payments/Customers actions
 * change — the picker copy says so.
 */
function SubscriptionRowChangeMethodAction({
  item,
}: {
  item: StripeSubscriptionRecord
}) {
  const setSubscriptionMethod = useSetSubscriptionPaymentMethod()
  const billingProfileId = item.billingProfile?.id ?? null

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <PaymentMethodPickerMenu
              clientBillingProfileId={billingProfileId}
              // This subscription's own method, not the customer's default —
              // billing it to the account default is a legitimate move.
              currentPaymentMethodId={readPaymentMethodId(
                item.paymentMethodSummary,
              )}
              isPending={setSubscriptionMethod.isPending}
              disabledReason="No billing profile linked to this customer"
              onSelect={(stripePaymentMethodId) =>
                setSubscriptionMethod.mutate({
                  stripeSubscriptionId: item.stripeSubscriptionId,
                  stripePaymentMethodId,
                })
              }
            />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-64">
          Repoints this subscription only, not the customer&apos;s default.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function SubscriptionsSection({
  initialClientId,
}: {
  initialClientId?: string
}) {
  const [filters, setFilters] = React.useState<StripeRecordsFilters>({
    page: 1,
    perPage: PER_PAGE,
    clientId: initialClientId,
  })
  const { data, isLoading } = useStripeSubscriptions(filters)
  const [columnVisibility, setColumnVisibility] =
    useSubscriptionColumnVisibility()

  const visibleColumns = SUBSCRIPTION_COLUMNS.filter(
    (column) => columnVisibility[column.key] ?? column.defaultVisible,
  )
  // Always-visible columns: Subscription, Cadence, Client, Actions.
  const columnCount = visibleColumns.length + 4

  return (
    <div className="space-y-4">
      <SyncToolbar
        object="subscription"
        title="Subscription sync"
        description="Mirror Stripe subscriptions for recurring billing, cadence changes, stored-payment follow-ons, and future subscription lifecycle monitoring beyond the initial checkout flow."
        advancedItems={<WidenRailsMenuItem />}
      />
      <SubscriptionsToolbar
        filters={filters}
        onFiltersChange={setFilters}
        billingPlans={data?.billingPlans ?? []}
        statuses={data?.statuses ?? []}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
      />
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Subscription</TableHead>
              <TableHead>Cadence</TableHead>
              <TableHead>Client</TableHead>
              {visibleColumns.map((column) => (
                <TableHead key={column.key}>{column.label}</TableHead>
              ))}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <EmptyRows
                label="Loading Stripe subscriptions…"
                colSpan={columnCount}
              />
            ) : null}
            {!isLoading && !data?.items.length ? (
              <EmptyRows
                label="No Stripe subscriptions found for these filters."
                colSpan={columnCount}
              />
            ) : null}
            {data?.items.map((item: StripeSubscriptionRecord) => (
              <TableRow key={item.id}>
                <TableCell className="align-top">
                  <div className="space-y-1">
                    <p className="font-medium">{item.status}</p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {item.stripeSubscriptionId}
                    </p>
                  </div>
                </TableCell>
                <TableCell className="align-top">
                  <div className="space-y-1 text-xs">
                    <p className="font-medium">
                      {item.interval
                        ? `${item.intervalCount ?? 1} ${item.interval}`
                        : 'No cadence stored'}
                    </p>
                  </div>
                </TableCell>
                <TableCell className="align-top">
                  <div className="space-y-0.5">
                    {item.client ? (
                      <ClientLinkCell client={item.client} />
                    ) : item.stripeCustomer &&
                      (item.stripeCustomer.name ||
                        item.stripeCustomer.email) ? (
                      <>
                        <p className="font-medium">
                          {item.stripeCustomer.name ??
                            item.stripeCustomer.email}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {item.stripeCustomer.name && item.stripeCustomer.email
                            ? item.stripeCustomer.email
                            : 'Stripe customer · no Rothology client'}
                        </p>
                      </>
                    ) : !item.stripeCustomerId ? (
                      <span className="text-xs text-muted-foreground">
                        No customer linked
                      </span>
                    ) : null}
                    {item.stripeCustomerId ? (
                      <Link
                        to="/backoffice/integrations/stripe"
                        search={{
                          tab: 'customers',
                          customerSearch: item.stripeCustomerId,
                        }}
                        title="View on the Customers tab"
                        className="block font-mono text-[11px] text-muted-foreground hover:text-primary hover:underline"
                      >
                        {item.stripeCustomerId}
                      </Link>
                    ) : null}
                  </div>
                </TableCell>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={cn('align-top', column.cellClassName)}
                  >
                    {column.render(item)}
                  </TableCell>
                ))}
                <TableCell className="align-top text-right">
                  <div className="flex justify-end">
                    <SubscriptionRowChangeMethodAction item={item} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <PaginationFooter
          page={data?.page ?? 1}
          totalPages={data?.totalPages ?? 1}
          total={data?.total ?? 0}
          isLoading={isLoading}
          onPageChange={(page) =>
            setFilters((current) => ({ ...current, page }))
          }
        />
      </Card>
    </div>
  )
}

function WebhookEventsSection({
  initialClientId,
}: {
  initialClientId?: string
}) {
  const [filters, setFilters] = React.useState<StripeWebhookEventsFilters>({
    page: 1,
    perPage: PER_PAGE,
    clientId: initialClientId,
    scope: 'all',
  })
  const [selectedEventId, setSelectedEventId] = React.useState<string | null>(
    null,
  )
  const [sheetOpen, setSheetOpen] = React.useState(false)

  const { data, isLoading } = useStripeWebhookEvents(filters)
  const scopeCounts = data?.scopeCounts ?? {
    all: 0,
    payments: 0,
    invoices: 0,
    subscriptions: 0,
    checkout: 0,
    unlinked: 0,
  }

  const handleRowClick = (eventId: string) => {
    setSelectedEventId(eventId)
    setSheetOpen(true)
  }

  const handleScopeChange = (scope: StripeWebhookEventScope) => {
    setFilters((current) => ({ ...current, scope, page: 1 }))
  }

  return (
    <Card className="w-full overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-sm font-semibold">
              Webhook events
            </CardTitle>
            <CardDescription className="text-xs">
              Immutable Stripe audit trail across checkout, invoice, payment,
              customer, and subscription events. Use this to verify what Stripe
              sent before diving into the mirrored object tabs above.
            </CardDescription>
          </div>
          <Badge
            variant="outline"
            className="border-sky-500/30 bg-sky-500/10 text-xs text-sky-700 dark:text-sky-400"
          >
            Audit trail
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <StripeWebhookScopeTabs
          scope={filters.scope ?? 'checkout'}
          counts={scopeCounts}
          onChange={handleScopeChange}
        />
        <StripeWebhookFilters filters={filters} onFiltersChange={setFilters} />
        <StripeWebhookEventsTable
          data={data?.items ?? []}
          isLoading={isLoading}
          pagination={{
            currentPage: data?.page ?? 1,
            totalPages: data?.totalPages ?? 1,
            total: data?.total ?? 0,
            onPageChange: (page) =>
              setFilters((current) => ({ ...current, page })),
          }}
          onRowClick={handleRowClick}
        />
        <StripeWebhookEventDetailSheet
          eventId={selectedEventId}
          open={sheetOpen}
          onOpenChange={setSheetOpen}
        />
      </CardContent>
    </Card>
  )
}

function AutomationsSection() {
  const navigate = Route.useNavigate()
  const { autoTab } = Route.useSearch()

  const [logFilters, setLogFilters] =
    React.useState<StripeAutomationLogsFilters>({
      page: 1,
      perPage: PER_PAGE,
    })

  const { data: automations = [], isLoading: automationsLoading } =
    useStripeAutomations()
  const { data: logsData, isLoading: logsLoading } =
    useStripeAutomationLogs(logFilters)
  const {
    data: databaseAutomationsData,
    isLoading: databaseAutomationsLoading,
  } = useStripeDatabaseAutomations()

  const handleAutoTabChange = (value: string) => {
    void navigate({
      search: (prev: StripeRouteSearch) => ({
        ...prev,
        autoTab: value as StripeRouteSearch['autoTab'],
      }),
      replace: true,
    })
  }

  return (
    <Tabs
      value={autoTab}
      onValueChange={handleAutoTabChange}
      className="w-full"
    >
      <TabsList>
        <TabsTrigger value="external">External</TabsTrigger>
        <TabsTrigger value="database">Database</TabsTrigger>
      </TabsList>

      <TabsContent value="external" className="mt-4 w-full space-y-4">
        <StripeAutomationsList
          automations={automations}
          isLoading={automationsLoading}
        />

        <Card className="w-full overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">
              Automation log
            </CardTitle>
            <CardDescription className="text-xs">
              Every automation run triggered by a Stripe webhook event, with its
              outcome and target record.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <StripeAutomationLogFilters
              filters={logFilters}
              onFiltersChange={setLogFilters}
              automations={automations}
            />
            <StripeAutomationLogsTable
              data={logsData?.items ?? []}
              isLoading={logsLoading}
              automations={automations}
              pagination={{
                currentPage: logsData?.page ?? 1,
                totalPages: logsData?.totalPages ?? 1,
                total: logsData?.total ?? 0,
                onPageChange: (page) =>
                  setLogFilters((current) => ({ ...current, page })),
              }}
            />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="database" className="mt-4 w-full">
        <StripeDatabaseAutomationsReport
          automations={databaseAutomationsData?.automations ?? []}
          races={databaseAutomationsData?.races ?? []}
          isLoading={databaseAutomationsLoading}
        />
      </TabsContent>
    </Tabs>
  )
}

function DriftSection() {
  const queryClient = useQueryClient()
  const { data: findings = [], isLoading } = useStripeDriftFindings()
  const { data: status } = useStripeDriftStatus()
  const runCheck = useRunStripeDriftCheck()

  const running = Boolean(status && !status.completedAt)
  const active = running || runCheck.isPending

  const previousBatchRef = React.useRef<{
    id: string
    completedAt: string | null
  } | null>(null)

  React.useEffect(() => {
    if (!status) return
    const previous = previousBatchRef.current
    if (
      previous &&
      previous.id === status.id &&
      !previous.completedAt &&
      status.completedAt
    ) {
      const open = status.successDetails?.open ?? 0
      toast.success(`Drift check finished · ${open} open`)
      queryClient.invalidateQueries({ queryKey: queryKeys.stripe.drift() })
    }
    previousBatchRef.current = {
      id: status.id,
      completedAt: status.completedAt,
    }
  }, [status, queryClient])

  return (
    <Card className="w-full overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-sm font-semibold">
              Mirror drift
            </CardTitle>
            <CardDescription className="text-xs">
              Findings are flagged automatically every night and are not
              repaired automatically — fix one with the sync actions on the
              other tabs, or by reprocessing the webhook event that should have
              applied it.
            </CardDescription>
          </div>
          <Button
            size="sm"
            disabled={active}
            className="h-8 gap-1.5 text-xs"
            onClick={() => runCheck.mutate()}
          >
            <RefreshCw className={cn('size-3.5', active && 'animate-spin')} />
            {active ? 'Checking…' : 'Run check now'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {active ? (
          <BatchProgressBlock
            totalRecords={status?.totalRecords ?? 0}
            successCount={status?.successCount ?? 0}
            failureCount={status?.failureCount ?? 0}
            label="Checking…"
          />
        ) : status ? (
          <p className="text-xs text-muted-foreground">
            Last checked {formatDateTime(status.completedAt)} ·{' '}
            {status.successCount} clients checked
            {status.failureCount > 0 ? ` · ${status.failureCount} failed` : ''}
          </p>
        ) : null}
        <StripeDriftFindingsTable data={findings} isLoading={isLoading} />
      </CardContent>
    </Card>
  )
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { tab, clientId, customerSearch } = Route.useSearch()
  const { data: driftFindings } = useStripeDriftFindings()
  const driftCount = driftFindings?.length ?? 0

  const handleTabChange = (value: string) => {
    void navigate({
      search: (prev: StripeRouteSearch) => ({
        ...prev,
        tab: value as StripeRouteSearch['tab'],
      }),
      replace: true,
    })
  }

  return (
    <div className="py-4">
      <PageTitle title="Stripe Integration" />

      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice">Backoffice</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice/integrations">Integrations</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Stripe</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mb-6 flex items-center gap-3">
        <div className="flex size-8 items-center justify-center rounded-xl bg-[#635bff]/10 text-[#635bff]">
          <CreditCard className="size-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Stripe Integration
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Backoffice view for Stripe customers, payments, invoices,
            subscriptions, and the webhook audit trail that feeds Rothology
            billing.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
        <div className="max-w-full overflow-x-auto">
          <TabsList>
            <TabsTrigger value="customers">Customer</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
            <TabsTrigger value="automations">Automations</TabsTrigger>
            <TabsTrigger value="drift" className="gap-1.5">
              Drift
              {driftCount > 0 ? (
                <span className="text-xs text-muted-foreground">
                  ({driftCount})
                </span>
              ) : null}
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="customers" className="mt-4 w-full">
          <CustomersSection
            initialClientId={clientId}
            initialSearch={customerSearch}
          />
        </TabsContent>
        <TabsContent value="payments" className="mt-4 w-full">
          <PaymentsSection initialClientId={clientId} />
        </TabsContent>
        <TabsContent value="invoices" className="mt-4 w-full">
          <InvoicesSection initialClientId={clientId} />
        </TabsContent>
        <TabsContent value="subscriptions" className="mt-4 w-full">
          <SubscriptionsSection initialClientId={clientId} />
        </TabsContent>
        <TabsContent value="webhooks" className="mt-4 w-full">
          <WebhookEventsSection initialClientId={clientId} />
        </TabsContent>
        <TabsContent value="automations" className="mt-4 w-full">
          <AutomationsSection />
        </TabsContent>
        <TabsContent value="drift" className="mt-4 w-full">
          <DriftSection />
        </TabsContent>
      </Tabs>
    </div>
  )
}
